import { useState, useEffect } from 'react'

export type Theme = 'light' | 'dark' | 'auto'

export const useTheme = () => {
  const [theme, setTheme] = useState<Theme>(() => {
    // ローカルストレージからテーマを取得
    const savedTheme = localStorage.getItem('theme') as Theme
    if (savedTheme && ['light', 'dark', 'auto'].includes(savedTheme)) {
      return savedTheme
    }
    return 'auto'
  })

  const [currentTheme, setCurrentTheme] = useState<'light' | 'dark'>('light')

  useEffect(() => {
    // テーマをローカルストレージに保存
    localStorage.setItem('theme', theme)
    
    // 実際のテーマを決定
    let actualTheme: 'light' | 'dark' = 'light'
    
    if (theme === 'auto') {
      // システムの設定を確認
      actualTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    } else {
      actualTheme = theme
    }
    
    setCurrentTheme(actualTheme)
    
    // HTML要素にクラスを適用
    const html = document.documentElement
    if (actualTheme === 'dark') {
      html.classList.add('dark')
    } else {
      html.classList.remove('dark')
    }
  }, [theme])

  // システムのテーマ変更を監視
  useEffect(() => {
    if (theme === 'auto') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      
      const handleChange = (e: MediaQueryListEvent) => {
        setCurrentTheme(e.matches ? 'dark' : 'light')
        const html = document.documentElement
        if (e.matches) {
          html.classList.add('dark')
        } else {
          html.classList.remove('dark')
        }
      }
      
      mediaQuery.addEventListener('change', handleChange)
      return () => mediaQuery.removeEventListener('change', handleChange)
    }
  }, [theme])

  const toggleTheme = () => {
    setTheme(prev => {
      switch (prev) {
        case 'light':
          return 'dark'
        case 'dark':
          return 'auto'
        case 'auto':
          return 'light'
        default:
          return 'light'
      }
    })
  }

  const getThemeIcon = () => {
    switch (theme) {
      case 'light':
        return '☀️'
      case 'dark':
        return '🌙'
      case 'auto':
        return '🖥️'
      default:
        return '☀️'
    }
  }

  const getThemeLabel = () => {
    switch (theme) {
      case 'light':
        return 'ライト'
      case 'dark':
        return 'ダーク'
      case 'auto':
        return '自動'
      default:
        return 'ライト'
    }
  }

  return {
    theme,
    currentTheme,
    toggleTheme,
    getThemeIcon,
    getThemeLabel
  }
} 